import type { SupabaseClient } from '@supabase/supabase-js';
import { VAPID_PUBLIC_KEY } from './config.js';

// Web Push registration for the staff app (M2 — docs/adr/0010-staff-mobile-pwa.md
// §3), the reception push.ts pattern (GC4) verbatim, pointed at
// staff_push_subscriptions instead of chat_push_subscriptions.

export type PushState = 'unsupported' | 'unconfigured' | 'denied' | 'off' | 'on';

function applicationServerKey(): Uint8Array {
  const b64 = VAPID_PUBLIC_KEY.replace(/-/g, '+').replace(/_/g, '/');
  const padded = b64 + '='.repeat((4 - (b64.length % 4)) % 4);
  const binary = atob(padded);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i++) bytes[i] = binary.charCodeAt(i);
  return bytes;
}

function pushSupported(): boolean {
  return 'serviceWorker' in navigator && 'PushManager' in window && 'Notification' in window;
}

export async function getPushState(): Promise<PushState> {
  if (!pushSupported()) return 'unsupported';
  if (!VAPID_PUBLIC_KEY) return 'unconfigured';
  if (Notification.permission === 'denied') return 'denied';
  const registration = await navigator.serviceWorker.getRegistration();
  const subscription = await registration?.pushManager.getSubscription();
  return subscription ? 'on' : 'off';
}

/** Subscribe this browser and persist the subscription (upsert by endpoint). */
export async function enablePush(sb: SupabaseClient): Promise<PushState> {
  const registration = await navigator.serviceWorker.getRegistration();
  if (!registration) return 'unsupported';

  const permission = await Notification.requestPermission();
  if (permission !== 'granted') return permission === 'denied' ? 'denied' : 'off';

  const subscription = await registration.pushManager.subscribe({
    userVisibleOnly: true,
    applicationServerKey: applicationServerKey() as BufferSource,
  });

  const {
    data: { user },
  } = await sb.auth.getUser();
  if (!user) throw new Error('session expirée');

  const json = subscription.toJSON();
  const { error } = await sb.from('staff_push_subscriptions').upsert(
    {
      endpoint: subscription.endpoint,
      user_id: user.id,
      p256dh: json.keys?.p256dh ?? '',
      auth: json.keys?.auth ?? '',
    },
    { onConflict: 'endpoint' },
  );
  if (error) {
    await subscription.unsubscribe().catch(() => undefined);
    throw new Error(error.message);
  }
  return 'on';
}

/** Unsubscribe this browser and drop its row. */
export async function disablePush(sb: SupabaseClient): Promise<PushState> {
  const registration = await navigator.serviceWorker.getRegistration();
  const subscription = await registration?.pushManager.getSubscription();
  if (subscription) {
    await sb.from('staff_push_subscriptions').delete().eq('endpoint', subscription.endpoint);
    await subscription.unsubscribe().catch(() => undefined);
  }
  return 'off';
}
